import { asset } from '@/lib/asset';
import { cn } from '@/lib/utils';
import type { NflTeamVisual } from '@/config/nfl-teams';

/**
 * Sepia line-art football helmet with the team's abbreviation as the shell
 * decal. The art is our own generated base render (one blank helmet, mirrored
 * per orientation, transparent background) with lettering composited on — no
 * team logos, nicknames, or wordmarks, so it stays trademark-safe. Assets live
 * in public/assets/helmets-lineart (source of truth: apps/nextjs, regenerated
 * via scripts/generate-helmet-lineart.py).
 *
 * Jest serves the build from a CDN subfolder, so the path goes through
 * asset() to resolve beside index.html rather than at the CDN root.
 *
 * `facing` picks the pre-rendered orientation so matchup rows can point
 * helmets at each other (away faces right, home faces left — Yahoo-style).
 */
export function NflHelmet({
  team,
  size = 28,
  facing = 'left',
  className,
}: {
  team: NflTeamVisual;
  size?: number;
  facing?: 'left' | 'right';
  className?: string;
}) {
  return (
    <span
      style={{ width: size, height: size }}
      className={cn('inline-block shrink-0 align-middle', className)}
    >
      <img
        src={asset(`assets/helmets-lineart/${team.abbr.toLowerCase()}-${facing}.webp`)}
        alt=""
        aria-hidden="true"
        width={size}
        height={size}
        className="h-full w-full object-contain"
      />
    </span>
  );
}
